/**
 * InfinitSales - Shell COMPARTILHADO do inbox (fase 4 do inbox do widget,
 * 2026-09-23): lista + janela de conversa, no mesmo layout dos outros inboxes
 * (NavSidebar + sidebar de 395px + area principal).
 *
 * Extraido do shell do Messenger (que era clone do Instagram, que era clone do
 * wa-business). O que muda entre canais e SO a fiacao, e ela vem inteira de
 * um ADAPTADOR (components/inbox/channelAdapters/<canal>.js): codigo do canal,
 * chave opaca, servico HTTP, modal de conexao, OAuth, icone, textos e as
 * capacidades (janela de 24 h, documento, audio, botoes). O 5o canal nasce
 * como adaptador, nao como 5a copia de 160 KB; e uma correcao aqui vale para
 * todos os canais que usam o shell.
 *
 * Canais neste shell: messenger, instagram, widget (pages/<canal>/Inbox.jsx
 * virou wrapper fino). Os dois inboxes de WhatsApp continuam com o shell
 * proprio (numero de origem, templates, inbox virtual, formato da Evolution):
 * registrado na matriz de canais como pendencia da extracao.
 */
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Loader2, Plug, UserCheck } from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';
import { useAuth } from '../../contexts/AuthContext';
import { useSSEEvent, SSE_EVENTS } from '../../contexts/SSEContext';
import NavSidebar from '../NavSidebar';
import NotificationBell from '../notifications/NotificationBell';
import SwipeToast from '../notifications/SwipeToast';
import useFeatureFlags from '../../hooks/useFeatureFlags';
import authService from '../../services/global/authService';
import { getChatState, getChatStatesBatch, resumeAgent } from '../../services/agents/agentService';
import ChatList from './ChatList';
import ChatWindow from './ChatWindow';
// Frentes C1, C2 e C4 (flags persistent_layout, inbox_store, chat_cache).
import { useInboxLayout } from './InboxLayout';
import { useChannelChats, setInboxScrollTopSilent } from '../../services/inbox/inboxStore';
import { getBootFlag } from '../../hooks/useFeatureFlags';
import {
  appendChatMessage, prefetchChatMessages, createHoverPrefetcher,
} from '../../services/inbox/chatCache';

const PAGE_SIZE = 30;

// Cache de módulo POR CANAL: o shell DESMONTA ao trocar para outro inbox e
// remonta na volta. Sem isto, cada volta era tela branca + spinner por dois
// round-trips (status e depois chats). Com ele, a volta renderiza a última
// lista na hora e revalida em background. Com inbox_store o espelho e o
// proprio store e este cache fica parado.
const shellCaches = {};
const cacheOf = (code) => {
  if (!shellCaches[code]) shellCaches[code] = { account: null, chats: [], hasMore: false, at: 0 };
  return shellCaches[code];
};

const ChannelInbox = ({ adapter }) => {
  const code = adapter.code;
  const prefix = adapter.prefix;
  const keyField = adapter.keyField;
  const service = adapter.service;
  const { colors, theme } = useTheme();
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  // Frente C1: com persistent_layout a NavSidebar mora no InboxLayout e este
  // shell so publica as props dela (ver efeito perto do render).
  const { layoutOwnsNav, setNavProps } = useInboxLayout();
  // Frente C4: cache de conversas abertas + prefetch no hover (decidido na
  // montagem, como o store da lista).
  const chatCacheOnRef = useRef(null);
  if (chatCacheOnRef.current === null) chatCacheOnRef.current = getBootFlag('chat_cache');
  const chatCacheOn = chatCacheOnRef.current;
  const shellCache = cacheOf(code);
  // Frente C2: com inbox_store, lista/hasMore/conta vivem no inboxStore (mesma
  // semantica do cache de modulo, que segue sendo a semente com a flag OFF).
  const listState = useChannelChats(code, {
    chats: shellCache.chats, hasMore: shellCache.hasMore, status: shellCache.account,
  });
  const { chats, setChats, hasMore, setHasMore, status: account, setStatus: setAccount } = listState;
  const listStateRef = useRef(listState);
  listStateRef.current = listState;
  const listBootRef = useRef({ cacheUsable: listState.cacheUsable, stale: listState.stale });
  const [selectedChat, setSelectedChat] = useState(null);
  // Espelho para callbacks estaveis (prefetch no hover) lerem a selecao atual.
  const selectedChatRef = useRef(null);
  selectedChatRef.current = selectedChat;
  // Spinner de tela cheia só na PRIMEIRA montagem da sessão (cache vazio).
  const [loading, setLoading] = useState(
    listState.fromStore ? !listBootRef.current.cacheUsable : shellCache.at === 0,
  );
  const [loadingMore, setLoadingMore] = useState(false);
  const [showConnection, setShowConnection] = useState(false);
  // OAuth (canais da Meta): erro vindo do redirect/troca do code, exibido no modal.
  const [oauthError, setOauthError] = useState(null);
  const [oauthExchanging, setOauthExchanging] = useState(false);
  // Passo 2 do OAuth do Messenger: Páginas devolvidas pelo connect-oauth
  // aguardando a escolha do usuário no modal.
  const [oauthPages, setOauthPages] = useState(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  // Estado de autopilot (mesma fonte GLOBAL do wa-business): o ChatList e o
  // ChatWindow precisam dele para mostrar o selo do agente numa conversa NOVA
  // respondida por autopilot ANTES de qualquer transferência. As chaves do
  // canal entram em autopilot_contacts como '{canal}:{id}'.
  const [autopilotContacts, setAutopilotContacts] = useState([]);
  const [autopilotMode, setAutopilotMode] = useState('off');
  const [blockedContacts, setBlockedContacts] = useState([]);
  const [activeAgentName, setActiveAgentName] = useState(null);
  // Handoff por contato: { [id cru]: { state, state_metadata } }. Chaveado pelo
  // id cru porque é o que o SSE deste canal carrega e o que o objeto de chat
  // expõe. A CONSULTA usa o contact_ref ('{canal}:{id}'), a chave de CRM.
  const [handoffStates, setHandoffStates] = useState({});
  // Aviso de propriedade: o contato saiu deste workspace enquanto o inbox
  // estava aberto (motor de propriedade). Espelho do QR e do Business.
  const [ownershipToast, setOwnershipToast] = useState(null);
  // D2 do sweep 2B: estado de handoff na LISTA (chip "Transferido" e as
  // pilulas de status), atras de flag.
  const { flags: featureFlags } = useFeatureFlags();
  const handoffNaLista = Boolean(featureFlags?.inbox_meta_handoff_list);
  // Paginação continua de onde o cache parou.
  const offsetRef = useRef(chats.length);
  // Espelho da lista para o handler de SSE consultar sem entrar nas deps.
  const chatsRef = useRef([]);
  const reloadTimerRef = useRef(null);
  const reloadingRef = useRef(false);
  // Filtros server-side do drawer: vivem num ref para o loadChats (e o reload
  // via SSE) sempre usarem o valor atual sem re-registrar callbacks. O filtro
  // proprio do canal (ex.: widget de origem) entra aqui tambem.
  const extraFiltersRef = useRef({ since: null, unanswered: false });
  // Busca DENTRO dos filtros (INBOX_SEARCH_WITHIN_FILTERS_ENABLED): o termo
  // da lupa so vai como `q` com filtro do drawer ativo (mesma regra dos 2
  // inboxes de WhatsApp).
  const searchRef = useRef({ q: '', searchMessages: false });
  const filtrosAtivos = useCallback(() => {
    const e = extraFiltersRef.current || {};
    return (e.label_ids || []).length > 0 || Boolean(e.member_id || e.since || e.created_from
      || e.created_to || e.unanswered) || (e.custom_fields || []).length > 0;
  }, []);
  // Filtro de CONEXAO do canal (analogo do numero de origem do WhatsApp): o
  // widget escolhido nos cards ou no seletor da lista. Chega pela querystring
  // (?widget_id=...) e pelo seletor.
  const [channelFilter, setChannelFilter] = useState(() => {
    if (!adapter.filterParam) return null;
    try { return new URLSearchParams(location.search).get(adapter.filterParam) || null; } catch { return null; }
  });
  const channelFilterRef = useRef(channelFilter);
  channelFilterRef.current = channelFilter;

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const refOf = useCallback((chat) => chat?.contact_ref
    || (chat?.[keyField] ? `${prefix}${chat[keyField]}` : ''), [keyField, prefix]);
  const rawIdOf = useCallback((chat) => chat?.[keyField] || null, [keyField]);
  const stripPrefix = useCallback((raw) => {
    const s = String(raw || '');
    return s.startsWith(prefix) ? s.slice(prefix.length) : s;
  }, [prefix]);

  const loadChats = useCallback(async (reset = false) => {
    if (reloadingRef.current) return;
    reloadingRef.current = true;
    try {
      const offset = reset ? 0 : offsetRef.current;
      const extra = { ...extraFiltersRef.current };
      if (searchRef.current.q && filtrosAtivos()) {
        extra.q = searchRef.current.q;
        extra.search_messages = searchRef.current.searchMessages;
      }
      if (adapter.filterParam && channelFilterRef.current) extra[adapter.filterParam] = channelFilterRef.current;
      const res = await service.getChats(PAGE_SIZE, offset, extra);
      const page = res?.data || [];
      setHasMore(Boolean(res?.has_more));
      offsetRef.current = offset + page.length;
      setChats((prev) => {
        if (reset) return page;
        // Dedup por id: recarga concorrente (SSE + scroll) pode trazer o mesmo
        // chat duas vezes.
        const seen = new Set(prev.map((c) => c.id));
        return [...prev, ...page.filter((c) => !seen.has(c.id))];
      });
      listStateRef.current.markFetched({
        page: reset ? 1 : (listStateRef.current.page || 1) + 1,
        hasMore: Boolean(res?.has_more),
        filters: { labelIds: [], extra },
      });
      return page;
    } catch (e) {
      if (e?.response?.status !== 404) {
        console.error(`[${code}] falha ao carregar conversas:`, e);
      }
    } finally {
      reloadingRef.current = false;
    }
    return [];
  }, [setChats, setHasMore, service, code, adapter.filterParam, filtrosAtivos]);

  // Recarga agrupada: uma rajada de mensagens de contatos novos vira UMA
  // requisicao, nao uma por mensagem.
  const scheduleReload = useCallback(() => {
    if (reloadTimerRef.current) clearTimeout(reloadTimerRef.current);
    reloadTimerRef.current = setTimeout(() => {
      reloadTimerRef.current = null;
      loadChats(true);
    }, 400);
  }, [loadChats]);

  useEffect(() => { chatsRef.current = chats; }, [chats]);
  useEffect(() => () => {
    if (reloadTimerRef.current) clearTimeout(reloadTimerRef.current);
  }, []);

  // Espelha lista/conta no cache de módulo a cada mudança — a próxima
  // montagem (volta de outro inbox) abre já populada.
  useEffect(() => {
    if (listState.fromStore) return;
    shellCaches[code] = { account, chats, hasMore, at: Date.now() };
  }, [account, chats, hasMore, listState.fromStore, code]);

  // Autopilot-config no boot (mesma rota do wa-business). O modo e o nome do
  // agente são GLOBAIS da conta; a lista traz as chaves do canal.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await authService.getAutopilotConfig();
        if (cancelled || !res?.data) return;
        setAutopilotContacts(res.data.autopilot_contacts || []);
        setAutopilotMode(res.data.autopilot_mode || 'off');
        setBlockedContacts(res.data.blocked_contacts || []);
        setActiveAgentName(res.data.active_agent_name || null);
      } catch { /* selo de agente é best-effort; não bloqueia o inbox */ }
    })();
    return () => { cancelled = true; };
  }, []);

  // Agente ligado/desligado por outro membro do mesmo workspace: mantém o selo
  // do cartão/header em dia sem reload.
  useSSEEvent(SSE_EVENTS.AUTOPILOT_CHANGED, (data) => {
    if (!data) return;
    if (Array.isArray(data.autopilot_contacts)) setAutopilotContacts(data.autopilot_contacts);
    if (data.autopilot_mode) setAutopilotMode(data.autopilot_mode);
    if (Array.isArray(data.blocked_contacts)) setBlockedContacts(data.blocked_contacts);
  }, []);

  // --- Handoff -------------------------------------------------------------
  // O backend emite tudo isto (chat_state_changed e CONTACT_TRANSFERRED); o
  // shell escuta para o operador saber que o agente parou de responder.

  // Handoff detectado no inbound (agente calou porque o contato já estava
  // transferido). Handler PRÓPRIO: o de mensagem descarta eventos de controle.
  useSSEEvent(SSE_EVENTS.NEW_MESSAGE, (data) => {
    if (data?.source !== code || data?.type !== 'chat_state_changed') return;
    const raw = data[keyField];
    if (!raw) return;
    if (data.state === 'handoff') {
      setHandoffStates((prev) => ({
        ...prev,
        [raw]: { state: 'handoff', state_metadata: data.state_metadata || {} },
      }));
    } else {
      setHandoffStates((prev) => {
        const next = { ...prev };
        delete next[raw];
        return next;
      });
    }
  }, [code, keyField]);

  // Transferência feita DURANTE o turno do agente (checagem pós-stream).
  useSSEEvent(SSE_EVENTS.CONTACT_TRANSFERRED, (data) => {
    if (data?.source !== code) return;
    const raw = data[keyField];
    if (!raw) return;
    setHandoffStates((prev) => ({
      ...prev,
      [raw]: { state: 'handoff', state_metadata: data.state_metadata || {} },
    }));
  }, [code, keyField]);

  // Agente retomado por outro membro: limpa o banner na hora. O payload carrega
  // `contact_phone`, que neste canal chega como contact_ref OU id cru —
  // limpa as duas formas para não depender de quem chamou a rota.
  useSSEEvent(SSE_EVENTS.AGENT_RESUMED, (data) => {
    const raw = data?.contact_phone;
    if (!raw) return;
    const id = stripPrefix(raw);
    setHandoffStates((prev) => {
      const next = { ...prev };
      delete next[id];
      delete next[raw];
      return next;
    });
  }, [stripPrefix]);

  // Motor de propriedade: o contato mudou de dono entre workspaces. Quem
  // perdeu a visao tira o cartao da lista (e fecha a conversa aberta, com
  // aviso); quem recebeu recarrega em lote (D17 do sweep 2B).
  //
  // O casamento e pela CHAVE OPACA (`contact_ref`), nunca pelo `phone` do
  // payload, que vem vazio para chave opaca. O proprio prefixo do ref ja diz o
  // canal. Sem contact_ref no evento o handler nao age.
  useSSEEvent(SSE_EVENTS.CONTACT_OWNERSHIP_CHANGED, (data) => {
    const myId = user?.id;
    if (!myId) return;
    const ref = String(data?.contact_ref || '');
    if (!ref.startsWith(prefix)) return;
    const casa = (c) => refOf(c) === ref;

    // hidden_for/visible_for cobrem multi-hop (transferencia reversa): listam
    // TODAS as particoes afetadas. Fallback para o shape antigo.
    const hiddenForMe = Array.isArray(data.hidden_for) && data.hidden_for.includes(myId);
    const visibleForMe = Array.isArray(data.visible_for) && data.visible_for.includes(myId);
    const lostLegacy = data.prev_owner === myId && data.new_owner !== myId;
    const receivedLegacy = data.receiver === myId || data.new_owner === myId;

    if (hiddenForMe || (lostLegacy && !visibleForMe)) {
      setChats((prev) => prev.filter((c) => !casa(c)));
      setSelectedChat((prev) => (prev && casa(prev) ? null : prev));
      const novoDono = data.assigned_to?.name;
      setOwnershipToast({
        description: novoDono
          ? `Novo responsável: ${novoDono}.`
          : 'Este contato agora é atendido por outro responsável.',
      });
    } else if (visibleForMe || receivedLegacy) {
      scheduleReload();
    }
  }, [user?.id, scheduleReload, prefix, refOf]);

  // Retomar o agente depois do handoff (paridade com os inboxes de WhatsApp).
  const handleResumeAgent = useCallback(async (ref) => {
    const raw = String(ref || '');
    const id = stripPrefix(raw);
    try {
      await resumeAgent(raw);
      setHandoffStates((prev) => {
        const next = { ...prev };
        delete next[id];
        delete next[raw];
        return next;
      });
    } catch (err) {
      console.error(`[${code} Inbox] resume agent:`, err);
    }
  }, [code, stripPrefix]);

  // Refs cobertos pelo ultimo lote POST /chats/state-batch. Frente C4: ao
  // abrir uma conversa que o lote ja cobriu, o estado de handoff e o que o
  // shell guarda em handoffStates (mantido ao vivo pelo SSE).
  const batchCoveredRef = useRef(new Set());

  const checkChatHandoffState = useCallback(async (raw, contactRef) => {
    if (!raw) return;
    const ref = contactRef || `${prefix}${raw}`;
    if (chatCacheOn && batchCoveredRef.current.has(ref)) return;
    try {
      const result = await getChatState(ref);
      if (result?.state === 'handoff') {
        setHandoffStates((prev) => ({
          ...prev,
          [raw]: { state: 'handoff', state_metadata: result.state_metadata || {} },
        }));
      } else {
        setHandoffStates((prev) => {
          const next = { ...prev };
          delete next[raw];
          return next;
        });
      }
    } catch { /* best-effort: sem estado, o banner só não aparece */ }
  }, [chatCacheOn, prefix]);

  // Efeito (e não handler de clique) para cobrir também o deep-link do sino.
  // Chaveado pela IDENTIDADE do contato, nao pelo objeto.
  const selectedRawId = rawIdOf(selectedChat);
  const selectedContactRef = selectedChat?.contact_ref || null;
  useEffect(() => {
    if (selectedRawId) checkChatHandoffState(selectedRawId, selectedContactRef);
  }, [selectedRawId, selectedContactRef, checkChatHandoffState]);

  // Estado de handoff da LISTA inteira em UMA request (espelho do lote do QR e
  // do Business). A chave enviada e o contact_ref OPACO, que a rota aceita.
  const refsDaPagina = useMemo(
    () => chats.map(refOf).filter(Boolean).join(','),
    [chats, refOf],
  );
  useEffect(() => {
    if (!handoffNaLista || loading || !refsDaPagina) return undefined;
    let cancelled = false;
    (async () => {
      const refs = refsDaPagina.split(',');
      try {
        const states = await getChatStatesBatch(refs);
        if (cancelled) return;
        batchCoveredRef.current = new Set(refs);
        const novos = {};
        Object.entries(states || {}).forEach(([ref, state]) => {
          if (state?.state !== 'handoff') return;
          novos[stripPrefix(ref)] = { state: 'handoff', state_metadata: state.state_metadata || {} };
        });
        if (Object.keys(novos).length > 0) {
          setHandoffStates((prev) => ({ ...prev, ...novos }));
        }
      } catch (err) {
        console.warn(`[${code}] lote de handoff falhou:`, err);
      }
    })();
    return () => { cancelled = true; };
  }, [handoffNaLista, loading, refsDaPagina, code, stripPrefix]);

  // Chaves em handoff no formato que o ChatList compara: o contact_ref opaco.
  const handoffChats = useMemo(
    () => Object.keys(handoffStates).map((k) => (k.startsWith(prefix) ? k : `${prefix}${k}`)),
    [handoffStates, prefix],
  );

  // Boot: status da conexao decide entre inbox e tela de conectar. O fetch de
  // chats sai EM PARALELO (especulativo, erro engolido): com conta conectada,
  // corta um round-trip inteiro da primeira pintura.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const boot = listBootRef.current;
      const chatsPromise = boot.cacheUsable && !boot.stale
        ? Promise.resolve()
        : loadChats(true).catch(() => {});
      try {
        const status = await service.getStatus();
        if (cancelled) return;
        const data = status?.data;
        setAccount(data);
        if (data?.connected) {
          await chatsPromise;
        } else {
          setShowConnection(true);
        }
      } catch (e) {
        if (!cancelled) {
          // 404 é o caminho NORMAL: canal desligado por flag ou nenhuma conta
          // conectada ainda. Só o resto é erro de verdade.
          if (e?.response?.status !== 404) {
            console.error(`[${code}] falha ao consultar status:`, e);
          }
          setShowConnection(true);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [loadChats, setAccount, service, code]);

  // O item "Conexão <canal>" do menu de perfil (NavSidebar) manda para cá
  // com state.openConnection quando ainda não há conta — abre direto o modal
  // de conectar em vez de largar o operador numa lista vazia.
  useEffect(() => {
    if (location.state?.openConnection) {
      setShowConnection(true);
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state, location.pathname, navigate]);

  // Volta do OAuth da Meta: redireciona para {rota}?code=...&state=... (ou
  // ?error=... se o cliente cancelou). O code é de uso único — limpa a URL
  // antes de qualquer coisa para F5 não reenviar. No Messenger a troca do
  // code NÃO finaliza a conta: devolve a lista de Páginas e o modal renderiza
  // o seletor (select-page conclui). Canal sem OAuth (widget) nao passa aqui.
  useEffect(() => {
    const oauth = adapter.oauth;
    if (!oauth) return;
    const params = new URLSearchParams(location.search);
    const codeParam = params.get('code');
    const err = params.get('error') || params.get('error_reason');
    if (!codeParam && !err) return;
    navigate(adapter.route, { replace: true });

    if (err) {
      setOauthError(oauth.cancelledText);
      setShowConnection(true);
      return;
    }

    let expected = null;
    try { expected = sessionStorage.getItem(oauth.stateKey); } catch { /* ignore */ }
    const got = params.get('state');
    if (expected && got !== expected) {
      setOauthError('Não foi possível validar a autorização. Tente conectar de novo.');
      setShowConnection(true);
      return;
    }

    (async () => {
      setOauthExchanging(true);
      try {
        const res = await service.connectOAuth(codeParam, `${window.location.origin}${adapter.route}`);
        if (oauth.twoStep) {
          const pages = res?.data?.pages || [];
          setOauthError(null);
          setOauthPages(pages);
          // Abre o modal já no seletor de Página (passo 2 do OAuth).
          setShowConnection(true);
        } else {
          const status = await service.getStatus();
          setAccount(status?.data);
          // Avisa o NavSidebar do status fresco: o ícone do canal aparece na
          // hora, sem esperar o TTL do cache nem um reload.
          adapter.publishStatus?.(status?.data);
          setOauthError(null);
          setShowConnection(false);
          await loadChats(true);
        }
      } catch (e) {
        const detail = e?.response?.data?.detail;
        setOauthError(
          (typeof detail === 'object' ? detail?.message : detail)
          || 'Não foi possível concluir a conexão. Tente de novo.'
        );
        setShowConnection(true);
      } finally {
        setOauthExchanging(false);
        try { sessionStorage.removeItem(oauth.stateKey); } catch { /* ignore */ }
      }
    })();
    // location.search só importa na montagem pós-redirect.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Deep-link do sino/toast/Kanban/painel: navega para a rota do canal com
  // state.openChat, ou deixa o pedido em sessionStorage.pendingOpenChat
  // (entrar no workspace de outro membro recarrega a pagina e perde o state).
  // Seleciona assim que a conversa aparecer na lista; se o contato esta fora
  // da pagina carregada, recarrega e busca no servidor.
  const pendingOpenChatRef = useRef(null);
  if (pendingOpenChatRef.current === null) {
    let pendente = false;
    try {
      const raw = sessionStorage.getItem('pendingOpenChat');
      if (raw) {
        const parsed = JSON.parse(raw);
        if (parsed && parsed[keyField]) {
          pendente = parsed;
          sessionStorage.removeItem('pendingOpenChat');
        }
      }
    } catch (_e) { /* storage indisponivel */ }
    pendingOpenChatRef.current = pendente;
  }
  const deepLinkTriedRef = useRef('');
  useEffect(() => {
    const alvo = location.state?.openChat || pendingOpenChatRef.current;
    if (!alvo) return;
    const casa = (c) => c.id === alvo.chat_id
      || (alvo[keyField] && c[keyField] === alvo[keyField]);
    const achado = chats.find(casa);
    if (achado) {
      setSelectedChat(achado);
      pendingOpenChatRef.current = false;
      if (location.state?.openChat) navigate(location.pathname, { replace: true, state: {} });
      return;
    }
    const chave = String(alvo.chat_id || alvo[keyField] || '');
    if (!chave || deepLinkTriedRef.current === chave) return;
    deepLinkTriedRef.current = chave;
    (async () => {
      const recarregado = await loadChats(true);
      if ((recarregado || []).some(casa)) return;
      try {
        const res = await service.searchChats(alvo[keyField] || chave);
        const fora = (res?.data || []).find(casa);
        if (fora) {
          setSelectedChat(fora);
          pendingOpenChatRef.current = false;
          if (location.state?.openChat) navigate(location.pathname, { replace: true, state: {} });
          return;
        }
      } catch (_e) { /* ignore */ }
      console.warn(`[${code}] deep-link: contato nao encontrado nesta conta`);
      pendingOpenChatRef.current = false;
    })();
  }, [chats, location.state, location.pathname, navigate, loadChats, keyField, service, code]);

  const handleLoadMore = async () => {
    if (loadingMore || !hasMore) return;
    setLoadingMore(true);
    await loadChats(false);
    setLoadingMore(false);
  };

  // Filtros do drawer (período / sem resposta / etapa / responsável): a rota
  // /chats aceita tudo — refaz o fetch do zero com os novos parâmetros.
  const handleExtraFiltersChange = useCallback((filters) => {
    extraFiltersRef.current = {
      since: filters?.since || null,
      created_from: filters?.created_from || null,
      created_to: filters?.created_to || null,
      unanswered: !!filters?.unanswered,
      custom_fields: filters?.custom_fields || [],
      label_ids: Array.isArray(filters?.label_ids) ? filters.label_ids : [],
      member_id: filters?.member_id || null,
    };
    loadChats(true);
  }, [loadChats]);

  // Termo da lupa (debounced na ChatList). Com filtro ativo, muda a lista.
  const handleSearchTermChange = useCallback((q, searchMessages) => {
    const prev = searchRef.current;
    const next = { q: q || '', searchMessages: Boolean(searchMessages) };
    searchRef.current = next;
    if (filtrosAtivos() && (prev.q !== next.q || prev.searchMessages !== next.searchMessages)) {
      loadChats(true);
    }
  }, [filtrosAtivos, loadChats]);

  // Filtro de conexao do canal (seletor de widget): refaz a lista.
  const handleChannelFilterChange = useCallback((value) => {
    setChannelFilter(value || null);
    channelFilterRef.current = value || null;
    setSelectedChat(null);
    loadChats(true);
  }, [loadChats]);

  // Mensagem nova: atualiza o preview e reordena sem refazer o fetch da lista.
  useSSEEvent(SSE_EVENTS.NEW_MESSAGE, (data) => {
    if (data?.source !== code) return;
    // Eventos de CONTROLE usam o mesmo tipo de evento SSE mas nao trazem
    // `message`: sem este filtro caem no updater e reordenam a conversa.
    if (!data.message) return;

    const conhecido = chatsRef.current.some((c) => c.id === data.chat_id);
    if (!conhecido) {
      scheduleReload();
      return;
    }

    // Frente C4: conversa em cache mas NAO aberta recebe a mensagem como
    // patch (append com dedup), em vez de perder a entrada.
    if (chatCacheOn && !data.message.from_me && data.chat_id !== selectedChat?.id) {
      appendChatMessage(code, data.chat_id, data.message, (m) => m?.id || m?.mid);
    }

    setChats((prev) => {
      const idx = prev.findIndex((c) => c.id === data.chat_id);
      if (idx === -1) return prev;
      // Anti-ressurreicao: contato transferido para fora deste workspace nao
      // volta para a lista de quem o perdeu.
      if (data.hidden_for_owner) return prev.filter((c) => c.id !== data.chat_id);

      const msg = data.message || {};
      const updated = {
        ...prev[idx],
        last_message_text: msg.text || prev[idx].last_message_text,
        last_message_at: data.chat_last_message_at || msg.timestamp || prev[idx].last_message_at,
        last_message_from_me: msg.from_me,
        // Mensagem do cliente reabre a janela de 24h — a lista precisa refletir
        // isso na hora, senao o cadeado do composer fica desatualizado.
        last_inbound_at: msg.from_me ? prev[idx].last_inbound_at : (data.chat_last_message_at || msg.timestamp || prev[idx].last_inbound_at),
        unread_count: msg.from_me
          ? prev[idx].unread_count
          : (prev[idx].id === selectedChat?.id ? 0 : (prev[idx].unread_count || 0) + 1),
      };
      const rest = prev.filter((_, i) => i !== idx);
      return [updated, ...rest];
    });

    // A conversa aberta precisa do last_inbound_at fresco para reabrir o
    // composer sem que o operador tenha de clicar fora e voltar.
    setSelectedChat((cur) => {
      if (!cur || cur.id !== data.chat_id) return cur;
      const msg = data.message || {};
      if (msg.from_me) return cur;
      return { ...cur, last_inbound_at: data.chat_last_message_at || msg.timestamp || cur.last_inbound_at };
    });
  }, [selectedChat?.id, scheduleReload, chatCacheOn, code]);

  // Estavel (useCallback): entra no itemData memoizado das linhas do ChatList.
  const handleSelectChat = useCallback((chat) => {
    setSelectedChat(chat);
    setChats((prev) => prev.map(
      (c) => (c.id === chat.id ? { ...c, unread_count: 0 } : c)
    ));
  }, [setChats]);

  // Frente C4: passar o mouse na linha (desktop, debounce 150 ms) ja busca a
  // primeira pagina de mensagens para o cache; o clique pinta na hora.
  const hoverPrefetcher = useMemo(() => createHoverPrefetcher(150), []);
  useEffect(() => () => hoverPrefetcher.cancel(), [hoverPrefetcher]);
  const handleHoverChat = useCallback((chat) => {
    const id = chat?.id;
    if (!id) return;
    if (selectedChatRef.current?.id === id) return;
    hoverPrefetcher.schedule(() => {
      prefetchChatMessages(code, id, () => adapter.loadMessages(id));
    });
  }, [hoverPrefetcher, code, adapter]);
  // Posicao da lista guardada sem re-render (so leitura na proxima montagem).
  const handleListScroll = useCallback((offset) => {
    setInboxScrollTopSilent(code, offset);
  }, [code]);

  const handleMessageSent = (chatId, text) => {
    setChats((prev) => {
      const idx = prev.findIndex((c) => c.id === chatId);
      if (idx === -1) return prev;
      const updated = {
        ...prev[idx],
        last_message_text: text,
        last_message_at: new Date().toISOString(),
        last_message_from_me: true,
      };
      return [updated, ...prev.filter((_, i) => i !== idx)];
    });
  };

  const handleConnected = async (data) => {
    setShowConnection(false);
    setOauthPages(null);
    setAccount({ connected: true, ...(data?.account || {}) });
    // getStatus fresco (shape com connected/can_connect/mode) para o NavSidebar
    // acender o ícone na hora.
    try {
      const status = await service.getStatus({ force: true });
      adapter.publishStatus?.(status?.data);
      setAccount(status?.data || { connected: true, ...(data?.account || {}) });
    } catch { /* ignore: o TTL do cache cobre no pior caso */ }
    await loadChats(true);
  };

  // Conectar: modal do canal (Meta) ou pagina propria de conexoes (widget).
  const openConnect = () => {
    if (adapter.connectRoute) {
      navigate(adapter.connectRoute);
      return;
    }
    setShowConnection(true);
  };

  const styles = {
    container: {
      display: 'flex', width: '100%', overflow: 'hidden',
      backgroundColor: colors.bgSecondary,
      fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
      ...(isMobile ? { height: '100dvh', overscrollBehavior: 'none' } : { height: '100vh', width: '100vw' }),
      ...(layoutOwnsNav && !isMobile ? { width: 'auto', flex: 1, minWidth: 0 } : {}),
    },
    mainArea: { flex: 1, display: 'flex', overflow: 'hidden' },
    sidebar: {
      width: isMobile ? '100%' : 395,
      minWidth: isMobile ? '100%' : 395,
      maxWidth: isMobile ? '100%' : 395,
      height: isMobile ? '100dvh' : '100vh',
      display: 'flex', flexDirection: 'column',
      backgroundColor: colors.bgPrimary,
      borderRight: isMobile ? 'none' : `1px solid ${colors.borderColor}`,
      paddingTop: isMobile ? 54 : 0,
      paddingBottom: isMobile ? 58 : 0,
    },
    brandBar: {
      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      padding: '0 10px 0 16px', height: 58, flexShrink: 0,
      borderBottom: `1px solid ${colors.borderColor}`,
      backgroundColor: colors.bgPrimary,
    },
    brandTitle: {
      fontSize: 20, fontWeight: 700, letterSpacing: '0.2px',
      color: colors.textPrimary,
      fontFamily: "'Segoe UI', -apple-system, BlinkMacSystemFont, Roboto, sans-serif",
    },
    brandLogo: {
      width: 32, height: 32, borderRadius: 8,
      backgroundColor: '#ffffff',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      overflow: 'hidden', flexShrink: 0,
      boxShadow: theme === 'dark' ? 'none' : '0 1px 4px rgba(0,0,0,0.18)',
      border: theme === 'dark' ? 'none' : `1px solid ${colors.borderColor}`,
    },
    connectBtn: {
      display: 'inline-flex', alignItems: 'center', gap: 5,
      padding: '5px 11px', borderRadius: 6, cursor: 'pointer',
      border: `1px solid ${adapter.color}59`, backgroundColor: `${adapter.color}1f`,
      color: adapter.color, fontSize: 12, fontWeight: 600,
    },
    placeholder: {
      flex: 1, display: 'flex', flexDirection: 'column',
      alignItems: 'center', justifyContent: 'center', gap: 12,
      color: colors.textSecondary, backgroundColor: colors.bgSecondary, padding: 24,
      textAlign: 'center',
    },
  };

  // NavSidebar EXIGE onPageChange: os itens sem `route` chamam a prop direto.
  // As páginas internas vivem nos shells de WhatsApp, então mandamos o destino
  // via state.targetPage. mobileHideNav esconde a barra inferior fixa quando
  // uma conversa está aberta no celular.
  const navProps = {
    currentPage: code,
    mobileHideNav: isMobile && !!selectedChat,
    onPageChange: (page) => {
      if (page === 'inbox') {
        navigate(user?.default_channel === 'wa_business' ? '/wa-business' : '/inbox');
        return;
      }
      navigate(
        user?.default_channel === 'wa_business' ? '/wa-business' : '/inbox',
        { state: { targetPage: page } },
      );
    },
    onOpenConnection: () => {
      navigate(
        user?.default_channel === 'wa_business' ? '/wa-business' : '/inbox',
        { state: { openConnection: true } },
      );
    },
  };
  useEffect(() => {
    if (layoutOwnsNav) setNavProps(navProps);
  });

  // Tela cheia so durante a troca do code do OAuth.
  if (oauthExchanging) {
    return (
      <div style={{ ...styles.container, alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: 10 }}>
        <Loader2 size={26} style={{ animation: 'spin 1s linear infinite' }} color={colors.textSecondary} />
        <span style={{ fontSize: 13, color: colors.textSecondary }}>
          {adapter.oauth?.exchangingText || 'Conectando...'}
        </span>
      </div>
    );
  }

  const showChatWindow = selectedChat && (!isMobile || selectedChat);
  const ConnectionModal = adapter.ConnectionModal;
  const ChannelIcon = adapter.Icon;
  const T = adapter.texts;

  return (
    <div style={styles.container}>
      {!layoutOwnsNav && (
        <NavSidebar
          currentPage={navProps.currentPage}
          mobileHideNav={navProps.mobileHideNav}
          onPageChange={navProps.onPageChange}
          onOpenConnection={navProps.onOpenConnection}
        />
      )}

      <div style={styles.mainArea}>
        {(!isMobile || !selectedChat) && (
          <div style={styles.sidebar}>
            {!isMobile && (
              <div style={styles.brandBar}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
                  <span style={styles.brandLogo}>
                    <img
                      src={process.env.PUBLIC_URL + '/logo.jpeg'}
                      alt="InfinitSales"
                      style={{ width: 20, height: 20, objectFit: 'contain' }}
                    />
                  </span>
                  <span style={styles.brandTitle}>InfinitSales</span>
                </div>
                <NotificationBell />
              </div>
            )}
            <ChatList
              adapter={adapter}
              chats={chats}
              selectedChat={selectedChat}
              onSelectChat={handleSelectChat}
              loading={loading}
              onLoadMore={handleLoadMore}
              hasMore={hasMore}
              loadingMore={loadingMore}
              account={account}
              onExtraFiltersChange={handleExtraFiltersChange}
              onSearchTermChange={handleSearchTermChange}
              channelFilter={channelFilter}
              onChannelFilterChange={handleChannelFilterChange}
              autopilotContacts={autopilotContacts}
              autopilotMode={autopilotMode}
              blockedContacts={blockedContacts}
              activeAgentName={activeAgentName}
              handoffChats={handoffNaLista ? handoffChats : []}
              onHoverChat={chatCacheOn ? handleHoverChat : undefined}
              initialScrollOffset={listState.fromStore ? listState.scrollTop : 0}
              onScrollOffset={listState.fromStore ? handleListScroll : undefined}
            />
          </div>
        )}

        {showChatWindow ? (
          <ChatWindow
            adapter={adapter}
            chat={selectedChat}
            chats={chats}
            account={account}
            onBack={() => setSelectedChat(null)}
            isMobile={isMobile}
            onMessageSent={handleMessageSent}
            autopilotContacts={autopilotContacts}
            autopilotMode={autopilotMode}
            blockedContacts={blockedContacts}
            activeAgentName={activeAgentName}
            isHandoff={!!handoffStates[rawIdOf(selectedChat)]}
            handoffMetadata={handoffStates[rawIdOf(selectedChat)]?.state_metadata || null}
            onResumeAgent={handleResumeAgent}
          />
        ) : (!isMobile && (
          <div style={styles.placeholder}>
            <ChannelIcon size={44} color={adapter.color} opacity={0.5} />
            {loading ? null : account?.connected ? (
              <>
                <span style={{ fontSize: 15 }}>Selecione uma conversa</span>
                <span style={{ fontSize: 12, maxWidth: 320, lineHeight: 1.5 }}>
                  {T.placeholderHint}
                </span>
              </>
            ) : (
              <>
                <span style={{ fontSize: 15 }}>{T.noAccount}</span>
                <button type="button" style={styles.connectBtn} onClick={openConnect}>
                  <Plug size={13} />
                  {T.connectButton}
                </button>
              </>
            )}
          </div>
        ))}
      </div>

      <SwipeToast
        isOpen={!!ownershipToast}
        icon={<UserCheck size={20} />}
        title="Contato transferido"
        description={ownershipToast?.description}
        onDismiss={() => setOwnershipToast(null)}
        accentColor="#2563EB"
      />

      {showConnection && ConnectionModal && (
        <ConnectionModal
          onClose={() => { setShowConnection(false); setOauthPages(null); }}
          onConnected={handleConnected}
          externalError={oauthError}
          pages={oauthPages}
        />
      )}
    </div>
  );
};

export default ChannelInbox;
